/**
 * useExamPermission — check a single exam-service permission.
 *
 * v4 note: the nexus-auth frontend SDK is JWT-only. Previous versions
 * dispatched to an in-browser Zanzibar gRPC client; that runtime has been
 * removed. This hook now reads permissions straight from the current
 * access token via `useExamAuth`, which mirrors the server's service-
 * scoped role/permission handling.
 */
import { useMemo } from 'react';
import { useExamAuth } from '@/hooks/useExamAuth';

type PermissionSource = 'jwt' | 'superadmin';

interface ExamPermissionResult {
  allowed: boolean | null;
  loading: boolean;
  error: Error | null;
  refresh: () => void;
  source: PermissionSource;
}

function matchesPermission(userPerms: string[], required: string): boolean {
  if (userPerms.includes(required)) return true;
  // Wildcard match — support both `resource:action:scope` and flat wildcard.
  if (userPerms.includes('*:*:*')) return true;
  if (userPerms.includes('*:*:*:*')) return true;
  return false;
}

export function useExamPermission(
  permission: string,
  _resourceId?: string,
): ExamPermissionResult {
  const { roles, permissions, isLoading } = useExamAuth();

  const allowed = useMemo(() => {
    if (isLoading) return null;
    if (roles.includes('system_admin') || roles.includes('super_admin')) return true;
    return matchesPermission(permissions, permission);
  }, [isLoading, roles, permissions, permission]);

  const source: PermissionSource =
    roles.includes('system_admin') || roles.includes('super_admin') ? 'superadmin' : 'jwt';

  // No async work happens anymore, so `refresh` is a no-op kept for API stability.
  return {
    allowed,
    loading: isLoading,
    error: null,
    source,
    refresh: () => {},
  };
}
